import { z } from "zod";

import {
	CompanyFilterSearchSchema,
	IndustrySchema,
} from "@/pages/Private/pages/CompanyEnrich/schema/companyEnrich";

/**
 * The company's search preset (PREC-3666): what a person search opens with until the user has
 * searched once. Countries are alpha-2 codes. Every array is present — an empty one is "no
 * filter", so applying the preset also clears a field it does not set.
 */
export const PersonSearchDefaultsSchema = z.object({
	positionQuery: z.array(z.string()),
	seniority: z.array(z.string()),
	department: z.array(z.string()),
	/** Person location. */
	countries: z.array(z.string()),
	/** Company location (HQ). */
	companyCountries: z.array(z.string()),
	companyFilterSearch: z.object({
		employees: z.array(z.string()),
		industries: z.array(IndustrySchema),
	}),
});

export type PersonSearchDefaults = z.infer<typeof PersonSearchDefaultsSchema>;

/**
 * A user's last search, shared by the list builder and the extension's Find leads. A surface
 * sends only the fields it shows; a key that is absent was never searched.
 */
export const PersonLastSearchSchema = z.object({
	positionQuery: z.array(z.string()).optional(),
	seniority: z.array(z.string()).optional(),
	department: z.array(z.string()).optional(),
	countries: z.array(z.string()).optional(),
	companyCountries: z.array(z.string()).optional(),
	companyStates: z.array(z.number()).optional(),
	companyCities: z.array(z.number()).optional(),
	excludePositionQuery: z.array(z.string()).optional(),
	excludeDepartment: z.array(z.string()).optional(),
	excludeCountries: z.array(z.string()).optional(),
	excludeDomains: z.array(z.string()).optional(),
	companyFilterSearch: z
		.object({
			employees: z.array(z.string()).optional(),
			industries: z.array(IndustrySchema).optional(),
			keywords: z.array(z.string()).optional(),
			regions: z.array(z.string()).optional(),
			exclude: CompanyFilterSearchSchema.shape.exclude,
		})
		.optional(),
});

export type PersonLastSearch = z.infer<typeof PersonLastSearchSchema>;

/** GET /search-defaults — and the answer of every write to it. */
export const SearchDefaultsInfoSchema = z.object({
	companyPreset: PersonSearchDefaultsSchema,
	lastSearch: PersonLastSearchSchema.nullable(),
	/** English names of every country code either search uses, by code. */
	countryNames: z.record(z.string(), z.string()),
});

export type SearchDefaultsInfo = z.infer<typeof SearchDefaultsInfoSchema>;
